import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOwner, errText } from "@/pages/admin/shared";
import { deskApi } from "@/lib/deskApi";
import { QueryError, retryTransient } from "@/lib/queryState";
import { MIN_N } from "@/lib/checkpointState";
import { Badge, EmptyState, Icon, Panel, RuleResult, Skeleton, StatCell, Switch, fmt, etAndLocal } from "@/sentinel";

const usd = (v) => (v == null ? "—" : fmt(v, "usd", { signed: true }));
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);
const legsText = (legs) =>
  (legs || []).map((l) => `${l.side === "long" ? "+" : "−"}${l.right}${fmt(l.strike, "strike")}`).join(" / ") || "—";

/** Progress toward the owner-set re-evaluation sample (n ≥ 60 settled paper trades). */
function Progress({ p }) {
  const frac = Math.max(0, Math.min(1, p?.fraction ?? 0));
  return (
    <div className="sn-stack" style={{ gap: 4, minWidth: 180, flex: "1 1 180px" }} data-testid="paper-progress">
      <span className="sn-stat-k">Re-evaluation at n ≥ {p?.target ?? 60}</span>
      <div role="progressbar" aria-valuemin={0} aria-valuemax={p?.target ?? 60} aria-valuenow={p?.n ?? 0}
        aria-label={`${p?.n ?? 0} of ${p?.target ?? 60} settled paper trades`}
        style={{ height: 6, borderRadius: "var(--radius-full)", background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", overflow: "hidden" }}>
        <div style={{ width: `${frac * 100}%`, height: "100%", background: "var(--provenance)" }} />
      </div>
      <span className="sn-small sn-muted sn-num">{p?.n ?? 0} / {p?.target ?? 60}{p?.reached ? " · reached, due for re-evaluation" : ""}</span>
    </div>
  );
}

const netText = (t) => {
  if (!t.triggered || t.net == null) return "—";
  return t.kind === "credit" ? `${fmt(-t.net, "price")} cr` : fmt(t.net, "price");
};

function TradesTable({ trades, spec }) {
  const scheduled = spec?.entry?.schedule === "cron";
  if (!trades?.length) {
    return (
      <EmptyState icon="Ban" title="No paper evaluations yet">
        {scheduled ? `The strategy is evaluated at ${etAndLocal(spec.entry.hhmm)} on every trading day.` : "The strategy is evaluated at its checkpoint on live sessions."}
      </EmptyState>
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sn-table" data-testid="paper-trades-table" aria-label="Paper trades">
        <thead>
          <tr><th>Session</th><th>Triggered</th><th>Direction</th><th>Legs</th><th className="r">Net</th><th className="r">Max P / L</th><th className="r">SPX close</th><th className="r">P&amp;L</th>{scheduled && <th className="r">1 tick worse</th>}<th>Reason</th></tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr key={t.id} data-testid={`paper-trade-${t.session_date}`}>
              <td className="id">{t.session_date}</td>
              <td>{t.triggered ? <RuleResult result="PASS">YES</RuleResult> : <RuleResult result="NA">NO</RuleResult>}</td>
              <td>{t.direction || "—"}</td>
              <td className="num">{legsText(t.legs)}</td>
              <td className="num">{netText(t)}</td>
              <td className="num">{t.triggered ? `${usd(t.max_profit)} / ${usd(t.max_loss == null ? null : -t.max_loss)}` : "—"}</td>
              <td className="num">{t.settlement ? fmt(t.settlement.spx_close, "index") : t.triggered ? "open" : "—"}</td>
              <td className="num" style={{ fontWeight: 600 }}>{t.settled ? usd(t.realized_pnl) : "—"}</td>
              {scheduled && <td className="num">{t.settled ? usd(t.realized_pnl_tick) : "—"}</td>}
              <td className="sn-small sn-muted" style={{ overflowWrap: "anywhere", minWidth: 160 }}>{t.reason || (t.triggered ? "rule matched" : "—")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One line on the frozen filter: F3's tercile cuts, the v4 event-day rule, the IM/spot cut or the signed-flow cuts in force. */
function FilterLine({ spec, cut, sfCut }) {
  const f = spec.filter || {};
  let text = null;
  if (f.cuts) text = `Frozen cuts ${f.feature} ${f.cuts.join(" / ")} (dev-train terciles)`;
  else if (f.name === "evt_only") text = "CPI, FOMC-decision and Nonfarm-Payrolls days only (FMP calendar + FOMC schedule)";
  else if (f.name === "im_hi") {
    const v = cut?.value;
    text = `IM/spot at ${f.hhmm} ≥ ${v == null ? "—" : v.toFixed(5)} (trailing 12-month ${f.pct}th percentile, refit quarterly; ${cut?.fit_window ? `fit ${cut.fit_window.join(" → ")}` : "fit pending"} · ${cut?.source || "—"})`;
  } else if (spec.engine === "ai") text = "Structure and strikes chosen by the model (1 lot, vertical or iron condor, ±60 pts, wings 5–50)";
  else if (f.name === "none") text = "No day filter";
  if (spec.sf) {
    const c = sfCut?.cuts;
    const cuts = c ? `${c[0].toFixed(4)} / ${c[1].toFixed(4)}` : "—";
    const sfText = `Near-the-money signed flow 09:30–${spec.sf.hhmm}, tercile cuts ${cuts} (trailing 12 months, refit quarterly; ${sfCut?.fit_window ? `fit ${sfCut.fit_window.join(" → ")}` : "fit pending"} · ${sfCut?.source || "—"})`;
    text = f.name === "sf_near_agree" ? `Trade only when flow agrees with the trend · ${sfText}` : sfText;
  }
  return (
    <span className="sn-small sn-faint sn-num">
      {text ? `${text} · ` : ""}exit {spec.exit} · commission ${spec.costs?.commission_per_leg}/leg
      {spec.research?.oof ? ` · research OOF n ${spec.research.oof.n}, mean ${usd(spec.research.oof.mean)}, 1 tick worse ${usd(spec.research.oof_tick?.mean)}` : ""}
    </span>
  );
}

function StrategyBlock({ s }) {
  const st = s.stats || {};
  const spec = s.spec || {};
  const scheduled = spec.entry?.schedule === "cron";
  // Below the project's minimum sample the averages are shown small, with the n, so they do not read as a result.
  const early = (st.n ?? 0) < MIN_N;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)" }} data-testid={`paper-strategy-${spec.id}`}>
      <div className="sn-stack" style={{ gap: 2 }}>
        <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{spec.name} <span className="sn-small sn-muted sn-num">v{spec.version}</span></span>
        <span className="sn-small sn-muted">{spec.description}</span>
        {spec.family && <span className="sn-small sn-muted">{spec.family} · entry {etAndLocal(spec.entry?.hhmm)}{scheduled ? " (scheduled evaluation)" : spec.entry?.run ? ` (Run ${spec.entry.run})` : ""}</span>}
        <FilterLine spec={spec} cut={s.im_cut} sfCut={s.sf_cut} />
      </div>
      <div className="sn-row" style={{ gap: "var(--space-6)", alignItems: "flex-start" }}>
        <StatCell size="sm" label="Evaluated" value={String(st.evaluations ?? 0)} sub={`${st.triggered ?? 0} triggered · ${st.open ?? 0} open`} />
        <StatCell size="sm" label="Settled n" value={String(st.n ?? 0)} />
        {early ? (
          <div className="sn-stat sn-stat-sm" data-testid="paper-early-stats">
            <span className="sn-stat-k">Mean P&L · hit rate</span>
            <span className="sn-small sn-muted sn-num">{usd(st.mean_pnl)} · {pct(st.hit_rate)}</span>
            <span className="sn-stat-sub">n = {st.n ?? 0}, too early</span>
          </div>
        ) : (
          <>
            <StatCell size="sm" label="Mean P&L" value={st.mean_pnl} kind="usd" signed sub={`n = ${st.n ?? 0}`} />
            <StatCell size="sm" label="Hit rate" value={pct(st.hit_rate)} sub={`n = ${st.n ?? 0}`} />
          </>
        )}
        <StatCell size="sm" label="Total P&L" value={st.total_pnl} kind="usd" signed />
        <StatCell size="sm" label="Lower 95% CI" value={st.lower_ci} kind="usd" signed sub={st.lower_ci == null ? "needs n ≥ 2" : "per trade"} data-testid="paper-lower-ci" />
        {scheduled && <StatCell size="sm" label="Mean, 1 tick worse" value={st.tick_worse_mean_pnl} kind="usd" signed sub={`n = ${st.tick_worse_n ?? 0}`} data-testid="paper-tick-mean" />}
        {spec.engine === "ai" && <StatCell size="sm" label="Minus mirror, 1 tick worse" value={st.ai_minus_control_tick_mean} kind="usd" signed sub={`n = ${st.control_n ?? 0} directional`} data-testid="paper-ai-vs-mirror" />}
        <Progress p={s.progress} />
      </div>
      <TradesTable trades={s.latest_trades} spec={spec} />
    </div>
  );
}

/** [group, strategies] in API order; the group is the research programme and entry time. */
function groupByEntry(strategies) {
  const out = [];
  for (const s of strategies || []) {
    const g = s.spec?.group || "Paper";
    const last = out[out.length - 1];
    if (last && last[0] === g) last[1].push(s);
    else out.push([g, [s]]);
  }
  return out;
}

/** AI desk switch, model, prompt version and this month's model spend (one bar for the group). */
function AiDeskBar({ status }) {
  const isOwner = useOwner();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (!status) return null;
  const toggle = async (on) => {
    setBusy(true);
    try {
      await deskApi.setAiDesk({ enabled: on });
      await qc.invalidateQueries({ queryKey: ["paper-strategies"] });
      toast.success(on ? "AI desk switched on" : "AI desk switched off");
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="sn-row" style={{ gap: "var(--space-4)", flexWrap: "wrap", alignItems: "center" }} data-testid="ai-desk-bar">
      <Switch label="AI desk decisions" checked={!!status.enabled} disabled={!isOwner || busy} onChange={toggle} data-testid="ai-desk-switch" />
      <span className="sn-small sn-muted">Decides at {(status.decision_times || []).map((t) => etAndLocal(t)).join(" / ")}.</span>
      {/* Model, prompt version, spend and the API key state are developer detail: folded away by default. */}
      <details className="sn-small sn-faint" data-testid="ai-desk-details">
        <summary style={{ cursor: "pointer" }}>Technical details</summary>
        <span className="sn-num" style={{ display: "block", marginTop: "var(--space-1)" }}>
          Claude Opus 5.5 · effort {status.effort} · prompt {status.prompt_version} ·
          model spend this month {fmt(status.month_spend_usd ?? 0, "usd")} of {fmt(status.budget_usd ?? 0, "usd")} ({status.month_calls ?? 0} calls)
          {status.configured === false ? " · ANTHROPIC_API_KEY not set" : ""}
        </span>
      </details>
    </div>
  );
}

/** Scoring page: live paper tracking of research candidates. Hidden when the backend has no endpoint. */
export function PaperStrategiesPanel() {
  const query = useQuery({ queryKey: ["paper-strategies"], queryFn: () => deskApi.paperStrategies(), retry: retryTransient });
  const { data, isLoading, isError } = query;
  if (!isLoading && !isError && data == null) return null;
  return (
    <Panel title="Paper strategies · all live sessions since each strategy started" icon="FlaskConical" data-testid="paper-strategies"
      meta={<Badge icon="FlaskConical" className="sn-prov" data-testid="paper-label">{data?.label || "PAPER · advisory only · not an ENTER signal"}</Badge>}>
      {isLoading ? <Skeleton lines={3} /> : isError && !data ? (
        <QueryError query={query} title="Paper strategies could not load" what="the paper strategies" />
      ) : (
        <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
          <p className="sn-small" style={{ margin: 0, fontWeight: 600 }} data-testid="paper-note">
            <Icon name="Info" size={14} /> {data.note} Re-evaluate a strategy once it has n ≥ {data.reevaluate_at_n} settled out-of-sample trades.
          </p>
          {groupByEntry(data.strategies).map(([group, list]) => (
            <div key={group} className="sn-stack" style={{ gap: "var(--space-4)" }} data-testid={`paper-group-${group}`}>
              <span className="sn-stat-k">{group}</span>
              {list[0]?.ai_desk && <AiDeskBar status={list[0].ai_desk} />}
              {list.map((s) => <StrategyBlock key={s.spec.id} s={s} />)}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/** Tonight desk: one line when a paper strategy triggered on this session. Renders nothing otherwise. */
export function PaperStrategyNote({ date }) {
  const { data } = useQuery({ queryKey: ["paper-strategies", date], queryFn: () => deskApi.paperStrategies(date), enabled: !!date, retry: false });
  const hits = (data?.on_date || []).filter((t) => t.triggered);
  if (!hits.length) return null;
  return (
    <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-2)" }} data-testid="paper-strategy-note">
      <Badge icon="FlaskConical" className="sn-prov">PAPER</Badge>
      <span>
        {hits.map((t) => `Paper strategy ${t.strategy_id} triggered (not a trade signal) · ${t.direction || "—"} ${legsText(t.legs)}`).join(" · ")}
      </span>
    </div>
  );
}
